import { freshnessLabel } from "../freshness";

/**
 * HOW OLD THE NUMBERS ARE, RENDERED IN ONE PLACE.
 *
 * ===================== WHAT WAS ACTUALLY DUPLICATED, AND WHAT WAS NOT ======
 * The audit counted four freshness clocks and three formatters (D1) and
 * recommended one formatter and one render. Half of that is wrong and the
 * module docblocks say why: earned divergence 9 defends `freshnessLabel`
 * (elapsed sense, for a strip that is always on screen) against `measuredOn`
 * (calendar day, for a measurement that can be weeks old) as two jobs, not one
 * drifting helper. `readAt` is a third job again: an absolute clock time beside
 * a specific figure. Collapsing those would make a facilitator do arithmetic to
 * answer "is this live", which is the only question the strip exists to answer.
 *
 * So the duplication is in the RENDERING, and this is the render. The shell owns
 * it, which is also what the state matrix says: the context bar is the one
 * element present above every OS surface, so it is the only place that can
 * state the freshness of the shared read without a second surface restating it.
 *
 * ===================== VERIFIED, NOT ASSUMED ==============================
 * The audit named duplicate freshness renders inside Coach's glance. They are
 * gone: `CoachGlance.jsx` reads `generated_at` only as the "now" for quiet
 * duration maths and renders no label of its own, and Coach's own stale notice
 * was removed in FE-4 with the removal documented at `Dashboard/index.jsx`.
 * `freshness.js` has exactly one production consumer and this is it.
 *
 * ===================== WHAT IS DELIBERATELY LEFT ==========================
 * Review's `queue read <date, time>`, Manage's two `measured <date>` lines and
 * Coach's drawer provenance sentence are renders of DIFFERENT facts (a second
 * envelope, two capability measurements, one figure's source) carrying copy
 * their own surfaces own. Pulling them into this component would need their
 * sentences rewritten, which is page-copy work and belongs to FE-9.3 and
 * FE-9.4. This file is what those chunks pin to.
 *
 * ===================== IT IS NOT A LIVE REGION =============================
 * No `role="status"`. The label changes on every poll tick, and a strip that
 * announced "updated 3 minutes ago" into a screen reader every few seconds
 * would talk over the surface the facilitator is actually working on. The stale
 * notice below IS a live region, because that one is news.
 */
export const Freshness = ({ generatedAt, now, className = "", testId }) => {
  const label =
    now === undefined
      ? freshnessLabel(generatedAt)
      : freshnessLabel(generatedAt, now);
  // An absent or malformed timestamp yields the empty string, and the clause is
  // dropped entirely rather than rendered as "updated Invalid Date".
  if (!label) return null;
  return (
    <p
      data-testid={testId}
      className={`whitespace-nowrap text-xs text-lv4 ${className}`.trim()}
    >
      {label}
    </p>
  );
};

/**
 * The read did not go through, and the numbers on screen are the previous ones.
 *
 * A LIVE REGION, unlike the label above, because it is a change of state rather
 * than a ticking clock: the facilitator was reading live numbers a moment ago
 * and is now reading stale ones, and nothing else on the surface says so.
 *
 * The sentence names what is safe (the numbers are real, they are just older)
 * rather than what failed, per the brief's rule that an error states what
 * remains true. It carries no retry control of its own: the shell polls, so the
 * next tick is the retry, and offering a button that duplicates a timer invents
 * an action the facilitator does not need to take.
 */
export const StaleNotice = ({ className = "", testId }) => (
  <p
    role="status"
    data-testid={testId}
    className={`text-xs text-lv4 ${className}`.trim()}
  >
    the last refresh did not go through. these numbers are from the one before
    it.
  </p>
);

export default Freshness;
